import { getZoomFactor } from './zoomPopper';

/**
 * Adjusts a MUI Popover / Menu paper element's top and left styles
 * to compensate for the global CSS zoom factor on document.documentElement.
 */
export const adjustPopoverPosition = (element: HTMLElement, zoom: number = getZoomFactor()): void => {
  if (zoom === 1 || !element || !element.style) return;

  const rawTopStr = element.style.top;
  const rawLeftStr = element.style.left;
  if (!rawTopStr || !rawLeftStr) return;

  const currentTop = parseFloat(rawTopStr);
  const currentLeft = parseFloat(rawLeftStr);
  if (isNaN(currentTop) || isNaN(currentLeft)) return;

  // Avoid re-adjusting already adjusted values
  const lastTop = (element as any)._zoomAdjustedTop;
  const lastLeft = (element as any)._zoomAdjustedLeft;
  if (lastTop === currentTop && lastLeft === currentLeft) {
    return;
  }

  console.log('[zoomPopover] Adjusting element:', {
    classes: element.className,
    rawTopStr,
    rawLeftStr,
    currentTop,
    currentLeft,
    lastTop,
    lastLeft,
    zoom,
  });

  // Parse transformOrigin to compute accurate origin offset
  const transformOrigin = element.style.transformOrigin || '';
  const parts = transformOrigin.split(' ').filter(Boolean);

  let originX = 0;
  let originY = 0;

  const elemW = element.offsetWidth || element.clientWidth || 0;
  const elemH = element.offsetHeight || element.clientHeight || 0;

  if (parts.length >= 1) {
    const p0 = parts[0];
    if (p0.endsWith('px')) {
      originX = parseFloat(p0);
    } else if (p0 === 'center') {
      originX = elemW / 2;
    } else if (p0 === 'right') {
      originX = elemW;
    }
  }

  if (parts.length >= 2) {
    const p1 = parts[1];
    if (p1.endsWith('px')) {
      originY = parseFloat(p1);
    } else if (p1 === 'center') {
      originY = elemH / 2;
    } else if (p1 === 'bottom') {
      originY = elemH;
    }
  }

  // MUI Popover computes:
  // rawTop = anchorOffset.top - originY
  // rawLeft = anchorOffset.left - originX
  // Since anchorOffset was in visual pixels (scaled by zoom),
  // we scale the anchorOffset back to layout coordinates by dividing by zoom.
  const adjustedTop = Math.round((currentTop + originY) / zoom - originY);
  const adjustedLeft = Math.round((currentLeft + originX) / zoom - originX);

  (element as any)._zoomAdjustedTop = adjustedTop;
  (element as any)._zoomAdjustedLeft = adjustedLeft;

  element.style.top = `${adjustedTop}px`;
  element.style.left = `${adjustedLeft}px`;
};

/**
 * Adjusts a MUI Tabs indicator element's left and width styles
 * to align perfectly under CSS zoom.
 */
export const adjustTabsIndicatorStyle = (element: HTMLElement, zoom: number = getZoomFactor()): void => {
  if (zoom === 1 || !element || !element.style) return;

  const rawLeftStr = element.style.left;
  const rawWidthStr = element.style.width;

  if (!rawLeftStr || !rawWidthStr) return;

  const currentLeft = parseFloat(rawLeftStr);
  const currentWidth = parseFloat(rawWidthStr);

  if (isNaN(currentLeft) || isNaN(currentWidth)) return;

  // Avoid re-adjusting already adjusted values
  const lastLeft = (element as any)._zoomAdjustedLeft;
  const lastWidth = (element as any)._zoomAdjustedWidth;
  if (lastLeft === currentLeft && lastWidth === currentWidth) {
    return;
  }

  const adjustedLeft = Math.round(currentLeft / zoom);
  const adjustedWidth = Math.round(currentWidth / zoom);

  (element as any)._zoomAdjustedLeft = adjustedLeft;
  (element as any)._zoomAdjustedWidth = adjustedWidth;

  element.style.left = `${adjustedLeft}px`;
  element.style.width = `${adjustedWidth}px`;
};

let observer: MutationObserver | null = null;
let isInitialized = false;

/**
 * Initializes a global DOM observer that intercepts and corrects positioning for all
 * MUI Popovers, Menus, Select dropdowns, DatePickers, and Tabs indicators under CSS zoom.
 */
export const initZoomPopover = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return;
  }
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  const handleReposition = () => {
    const activePopovers = document.querySelectorAll<HTMLElement>(
      '.MuiPopover-paper, .MuiMenu-paper, .zoom-adjusted-menu-paper',
    );
    for (let i = 0; i < activePopovers.length; i++) {
      adjustPopoverPosition(activePopovers[i]);
    }
    const activeIndicators = document.querySelectorAll<HTMLElement>('.MuiTabs-indicator');
    for (let i = 0; i < activeIndicators.length; i++) {
      adjustTabsIndicatorStyle(activeIndicators[i]);
    }
  };

  observer = new MutationObserver(mutations => {
    let needsAdjustment = false;
    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];
      if (mutation.type === 'childList') {
        needsAdjustment = true;
        break;
      } else if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        const target = mutation.target as HTMLElement;
        if (
          target &&
          target.classList &&
          (target.classList.contains('MuiPopover-paper') ||
            target.classList.contains('MuiMenu-paper') ||
            target.classList.contains('zoom-adjusted-menu-paper') ||
            target.classList.contains('MuiTabs-indicator'))
        ) {
          needsAdjustment = true;
          break;
        }
      }
    }

    if (needsAdjustment) {
      handleReposition();
    }
  });

  const startObserving = () => {
    if (document.body) {
      observer!.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
      handleReposition();
    }
  };

  if (document.body) {
    startObserving();
  } else {
    window.addEventListener('DOMContentLoaded', startObserving, { once: true });
  }

  window.addEventListener('resize', handleReposition, { passive: true });
  window.addEventListener('scroll', handleReposition, { capture: true, passive: true });
};

// Automatically initialize in browser environments
if (typeof window !== 'undefined') {
  initZoomPopover();
}
